import React, { useState } from 'react';
import { Package, Search, CheckCircle, Truck, Clock, X, ChevronDown, ChevronUp, Download, ShoppingBag } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { showToast } from '../../utils/toast';

interface OrdersPanelProps {
  onNavigate: (section: string, data?: any) => void;
}

const OrdersPanel: React.FC<OrdersPanelProps> = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  
  const { orders, loading, cancelOrder } = useOrders();

  // Filter orders based on search term and status filter
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.items.some(item => item.product.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = !statusFilter || order.status === statusFilter;
    
    return matchesSearch && matchesFilter;
  });

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await cancelOrder(orderId);
    } catch (error) {
      console.error('Error cancelling order:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge-warning flex items-center space-x-1"><Clock size={14} /><span>Pending</span></span>;
      case 'confirmed':
        return <span className="badge-info flex items-center space-x-1"><CheckCircle size={14} /><span>Confirmed</span></span>;
      case 'processing':
        return <span className="badge-secondary flex items-center space-x-1"><Package size={14} /><span>Processing</span></span>;
      case 'shipped':
        return <span className="badge-primary flex items-center space-x-1"><Truck size={14} /><span>Shipped</span></span>;
      case 'delivered':
        return <span className="badge-success flex items-center space-x-1"><CheckCircle size={14} /><span>Delivered</span></span>;
      case 'cancelled':
        return <span className="badge-error flex items-center space-x-1"><X size={14} /><span>Cancelled</span></span>;
      default:
        return <span className="badge-secondary">{status}</span>;
    }
  };

  // Order Details Modal
  const OrderDetailsModal = () => {
    if (!selectedOrder) return null;
    
    const order = orders.find(o => o.id === selectedOrder);
    if (!order) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-custom">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Order #{order.id.substring(0, 8)}
            </h3>
            <button
              onClick={() => setSelectedOrder(null)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Order Status */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Order Status</p>
                  <div className="mt-1">
                    {getStatusBadge(order.status)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Order Date</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {new Date(order.orderDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {order.paymentMethod}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
                  <p className="font-bold text-gray-900 dark:text-gray-100">
                    ${order.total.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Order Items */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Order Items</h4>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
                      {item.product.images[0] ? (
                        <img 
                          src={item.product.images[0]} 
                          alt={item.product.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="text-gray-400 dark:text-gray-500" size={24} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{item.product.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Qty: {item.quantity}</p>
                      {item.product.prescriptionRequired && (
                        <span className="badge-warning text-xs">
                          Prescription Required
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">${(item.product.price * item.quantity).toFixed(2)}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">${item.product.price.toFixed(2)} each</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Shipping Address */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Shipping Address</h4>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <p className="text-gray-800 dark:text-gray-200">
                  {order.shippingAddress.street}<br />
                  {order.shippingAddress.city}, {order.shippingAddress.region}<br />
                  {order.shippingAddress.country}<br />
                  Phone: {order.shippingAddress.phone}
                </p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  showToast.system.success('Invoice downloaded');
                  setSelectedOrder(null);
                }}
                className="btn-secondary flex items-center space-x-2"
              >
                <Download size={16} />
                <span>Download Invoice</span>
              </button>
              
              {(order.status === 'pending' || order.status === 'confirmed') && (
                <button
                  onClick={() => {
                    handleCancelOrder(order.id);
                    setSelectedOrder(null);
                  }}
                  className="btn-error"
                >
                  Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">My Orders</h2>
        <button
          onClick={() => onNavigate('shop')}
          className="btn-primary flex items-center space-x-2"
        >
          <ShoppingBag size={16} />
          <span>Continue Shopping</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-base pl-9 w-full"
          />
        </div>
        
        <select
          value={statusFilter || ''}
          onChange={(e) => setStatusFilter(e.target.value || null)}
          className="select-base w-full sm:w-auto"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-green-600 dark:border-green-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading your orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No orders found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm || statusFilter
                ? 'No orders match your search criteria'
                : "You haven't placed any orders yet"}
            </p>
            <button
              onClick={() => onNavigate('shop')}
              className="btn-primary"
            >
              Browse Products
            </button>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const isExpanded = expandedOrders.has(order.id);
            
            return (
              <div 
                key={order.id} 
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-200"
              >
                {/* Order Header */}
                <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750" onClick={() => toggleOrderExpansion(order.id)}>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        Order #{order.id.substring(0, 8)}
                      </h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Placed on {new Date(order.orderDate).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <p className="font-bold text-gray-900 dark:text-gray-100">${order.total.toFixed(2)}</p>
                    <button className="text-gray-400 dark:text-gray-500">
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                </div>
                
                {/* Order Details (Expanded) */}
                {isExpanded && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
                    <div className="space-y-3 mb-4">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Items</h4>
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-800 dark:text-gray-200">{item.quantity}x</span>
                            <span className="text-gray-800 dark:text-gray-200">{item.product.name}</span>
                          </div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            ${(item.product.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order.id);
                        }}
                        className="btn-secondary btn-sm"
                      >
                        View Details
                      </button>
                      
                      {(order.status === 'pending' || order.status === 'confirmed') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelOrder(order.id);
                          }}
                          className="btn-error btn-sm"
                        >
                          Cancel Order
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && <OrderDetailsModal />}
    </div>
  );
};

export default OrdersPanel;